import './PacingPresetTableDashboard.scss';
import cn from 'classnames';
import Papa from 'papaparse';
import { MdOutlineCalendarToday, MdOutlineFileDownload } from 'react-icons/md';
import {
  Dispatch,
  ReactElement,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import { KpiInfo } from '@blisspointmedia/bpm-types/dist/Kpis';
import {
  MetricsTablePreset,
  PACING as PACING_MEDIATYPE
} from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { normalizeSourceKey } from '@blisspointmedia/bpm-types/dist/Pacing';
import { IconButton } from '@Components/IconButton';
import { Background, Design, Size } from '@Components/designConstants';
import { FilterBar } from '@Components/FilterBar/FilterBar';
import { Skeleton, TableSkeleton } from '@Components/Skeleton';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { MetricsTableWithPresets } from '@Components/MetricsTable/MetricsTableWithPresets';
import { MetricsTableHandle } from '@Components/MetricsTable/MetricsTable';
import {
  EditTablePresetModal,
  GroupedColumnPicker
} from '@Pages/CrossChannel/EditTablePresetModal';
import { PresetDropdown } from '@Components/PresetDropdown/PresetDropdown';
import { RenameModal } from '@Components/RenameModal/RenameModal';
import { ColumnMetaDataMap } from '@Components/MetricsTable/metrics-table-utils';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { download } from '@src/utils/download-utils';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { CrossChannelPacingTableData, TableView, TableViewType } from './pacing-constants';
import { buildOverTimeSuperHeaderRow } from './pacingUtils';
import { getPacingDataFetchKey } from './pacingFetchKeys';
import {
  buildKpiToKey,
  pacingDimensionColumnMetaDataMap,
  pacingMetricColumnMetaDataMap
} from './pacingMetricsTableConfig';
import {
  PACING_METRIC_OPTIONS,
  PACING_VARIANT_OPTIONS,
  PacingMetric,
  PacingPickerOption,
  pacingHeaderLabel,
  pacingShowKpiForMetric,
  pacingShowSourceForMetric,
  pacingVariantOptionsForMetric,
  parsePacingDataVarName,
  toPacingDataVarName
} from './pacingPresetColumnPicker';
import { buildOverTimePivot } from './pacingOverTimeConfig';
import { SaveTablePresetBody } from './use-pacing';

interface PacingPresetTableDashboardProps {
  company: string;
  pacingTableDataContent: CrossChannelPacingTableData | undefined;
  tableDataReady: boolean;
  /** Whole-number percent of the way today is through the selected date range, clamped to [0, 100]
   *  (0 before it starts, 100 once it has ended). Rendered as a read-only indicator above the table. */
  timePeriodProgress: number;
  /** "Summary" | "Over Time" — the over-time view pivots dimension buckets into super-header column
   *  groups with dates as rows. State lives in Pacing and is forwarded here to render the toggle. */
  tableView?: TableViewType;
  setTableView?: (view: TableViewType) => void;
  /** Grouping dimensions for the preset modal — the static pacing dims merged with the company's custom
   *  segments (built in Pacing from filterData). Defaults to the static set. */
  dimensionColumnMetaDataMap?: ColumnMetaDataMap;
  kpi: string;
  /** KPI metadata for the preset editor's per-column KPI dropdown. */
  kpiMetaData: Record<string, KpiInfo>;
  /** The page source picker's selection — the fetch buckets Global columns resolve to. */
  sources: string[];
  /** Analytics source options for the preset editor's per-column Source dropdown. */
  sourceOptions: PacingPickerOption[];
  tablePresets: MetricsTablePreset[] | undefined;
  tablePreset: MetricsTablePreset | undefined;
  fetchingTablePresets: boolean;
  getTablePreset: (p: {
    id: number;
    company: string;
    mediatype: string;
  }) => Promise<MetricsTablePreset>;
  saveTablePreset: (body: SaveTablePresetBody) => Promise<{ id: number; name: string }>;
  deleteTablePreset: (p: { id: number; mediatype: string }) => Promise<void>;
  applyTablePreset: (name: string, id?: number) => void;
}

// Base of the pacing Metric + Variant picker (mapping lives in pacingPresetColumnPicker); the
// component below adds the runtime sourceOptions.
const PACING_GROUPED_COLUMN_PICKER_BASE = {
  primaryLabel: 'Metric',
  secondaryLabel: 'Variant',
  primaryOptions: PACING_METRIC_OPTIONS,
  secondaryOptions: PACING_VARIANT_OPTIONS,
  secondaryOptionsForPrimary: pacingVariantOptionsForMetric,
  parse: (dataVarName: string) => {
    const { metric, variant } = parsePacingDataVarName(dataVarName);
    return { primary: metric, secondary: variant };
  },
  toDataVarName: (primary: string, secondary: string) =>
    toPacingDataVarName(primary as PacingMetric, secondary),
  showKpiForPrimary: pacingShowKpiForMetric,
  showSourceForPrimary: pacingShowSourceForMetric,
  headerLabel: (
    primary: string,
    secondary: string,
    kpiDisplayName?: string,
    sourceDisplayName?: string
  ) => pacingHeaderLabel(primary as PacingMetric, secondary, kpiDisplayName, sourceDisplayName)
};

const TimePeriodProgressIndicator = ({ progress }: { progress: number }): ReactElement => (
  <div className='pacing-time-period-progress'>
    <MdOutlineCalendarToday />
    <span>{`${progress}% through time period`}</span>
  </div>
);

export const PacingPresetTableDashboard = ({
  company,
  pacingTableDataContent,
  tableDataReady,
  timePeriodProgress,
  tableView = TableView.SUMMARY,
  setTableView,
  dimensionColumnMetaDataMap = pacingDimensionColumnMetaDataMap,
  kpi,
  kpiMetaData,
  sources,
  sourceOptions,
  tablePresets,
  tablePreset,
  fetchingTablePresets,
  getTablePreset,
  saveTablePreset,
  deleteTablePreset,
  applyTablePreset
}: PacingPresetTableDashboardProps): ReactElement => {
  const metricsTableRef = useRef<MetricsTableHandle>(null);
  const [showEditTablePresetModal, setShowEditTablePresetModal] = useState(false);
  const [showNewTablePresetModal, setShowNewTablePresetModal] = useState(false);
  const [showRenameTablePresetModal, setShowRenameTablePresetModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState<MetricsTablePreset | undefined>(undefined);
  const [tablePresetChanges, setTablePresetChanges] = useState<MetricsTablePreset | undefined>(
    undefined
  );
  // Search/Advanced filter predicate produced by the FilterBar, applied to table rows.
  const [filter, setFilter] = useState<{ filter: (line: Record<string, any>) => boolean }>();

  const data = useMemo(() => pacingTableDataContent?.data ?? [], [pacingTableDataContent]);
  const overTime = tableView === TableView.OVER_TIME;

  // Summary render map = metric columns + the (segment-augmented) dimension columns.
  const summaryColumnMetaDataMap = useMemo<ColumnMetaDataMap>(
    () => ({ ...pacingMetricColumnMetaDataMap, ...dimensionColumnMetaDataMap }),
    [dimensionColumnMetaDataMap]
  );

  const activeKpiKey = useMemo(() => buildKpiToKey(kpi || ''), [kpi]);
  const activeSourceKey = useMemo(() => normalizeSourceKey(sources), [sources]);
  const summaryDataFetchKey = useMemo(
    () => getPacingDataFetchKey(activeKpiKey, activeSourceKey),
    [activeKpiKey, activeSourceKey]
  );

  // The editor's per-column Source dropdown offers Platform plus the company's analytics sources.
  const groupedColumnPicker = useMemo<GroupedColumnPicker>(
    () => ({
      ...PACING_GROUPED_COLUMN_PICKER_BASE,
      sourceOptions: [{ label: 'Platform', value: 'platform' }, ...sourceOptions]
    }),
    [sourceOptions]
  );

  // Over-time view: pivot the per-(bucket x date) rows into one-row-per-date + bucket super-headers.
  // The FilterBar predicate runs on the RAW rows (which still carry the bucket dimensions) before the
  // pivot, so filtering removes whole bucket column-groups; the rendered rows then carry only Date.
  const selectedColumns = useMemo(
    () =>
      (tablePreset?.dataColumns ?? []).map(col => ({
        dataVarName: col.dataVarName,
        decimals: col.decimals,
        kpi: col.kpi,
        source: col.source,
        label: col.label
      })),
    [tablePreset]
  );
  const overTimePivot = useMemo(() => {
    if (!overTime) {
      return undefined;
    }
    const filteredRows = filter ? data.filter(row => filter.filter(row.dimensions)) : data;
    return buildOverTimePivot(filteredRows, selectedColumns, kpi, sources);
  }, [overTime, data, filter, selectedColumns, kpi, sources]);

  // Over-time renders a synthetic preset: Date as the only left dimension, the pivot's bucket x variant
  // columns, and one super-header per bucket.
  const renderPreset = useMemo<MetricsTablePreset | undefined>(() => {
    if (!overTime || !overTimePivot || !tablePreset) {
      return tablePreset;
    }
    return {
      ...tablePreset,
      dimensionColumns: [{ dimensionTypeName: 'Date', dimensionVarName: 'Date', id: 'date' }],
      dataColumns: overTimePivot.dataColumns,
      superHeaders: overTimePivot.superHeaders
    };
  }, [overTime, overTimePivot, tablePreset]);

  // Rendered column map: over-time merges the pivot's per-bucket columns; summary uses the
  // metric+dimension map.
  const renderColumnMetaDataMap = useMemo<ColumnMetaDataMap>(
    () =>
      overTime && overTimePivot
        ? { ...dimensionColumnMetaDataMap, ...overTimePivot.columnMetaDataMap }
        : summaryColumnMetaDataMap,
    [overTime, overTimePivot, dimensionColumnMetaDataMap, summaryColumnMetaDataMap]
  );

  const handleAddPreset = useCallback(() => {
    // Seed the new preset from the currently applied preset so EditTablePresetModal always
    // receives dimensionColumns/dataColumns arrays (it indexes them unguarded on render);
    // `?? []` keeps it safe when no preset is applied yet.
    setTablePresetChanges({
      ...(tablePreset ?? {}),
      id: 'new',
      name: 'My New Table Preset',
      dimensionColumns: tablePreset?.dimensionColumns ?? [],
      dataColumns: tablePreset?.dataColumns ?? []
      // id 'new' bridges into the MetricsTablePreset-typed edit state the shared modal expects.
    } as unknown as MetricsTablePreset);
    setShowNewTablePresetModal(true);
    Mixpanel.track(MxE.ModalOpened, { page: PAGE_NAMES.PACING, modal: 'new table preset' });
  }, [tablePreset]);

  const handleEditPreset = useCallback(
    (preset: MetricsTablePreset) => {
      (async () => {
        const fetched = await getTablePreset({
          id: preset.id,
          company,
          mediatype: PACING_MEDIATYPE
        });
        setTablePresetChanges(fetched);
        setShowEditTablePresetModal(true);
        Mixpanel.track(MxE.ModalOpened, { page: PAGE_NAMES.PACING, modal: 'edit table preset' });
      })();
    },
    [company, getTablePreset]
  );

  const handleSaveAndApply = useCallback(
    (changes: MetricsTablePreset) => {
      (async () => {
        const isExisting = changes.id > 0;
        const saved = await saveTablePreset({
          company,
          companyToUseInDB: company,
          id: isExisting ? changes.id : 'new',
          name: changes.name,
          mediatype: PACING_MEDIATYPE,
          preset: changes,
          temporary: changes.temporary
        });
        applyTablePreset(saved.name, saved.id);
        setShowEditTablePresetModal(false);
        setShowNewTablePresetModal(false);
        Mixpanel.track(isExisting ? MxE.RecordSaved : MxE.RecordCreated, {
          page: PAGE_NAMES.PACING,
          entity: 'table preset'
        });
      })();
    },
    [applyTablePreset, company, saveTablePreset]
  );

  // Build a CSV from the rows the table exposes through its ref handle (getExportedData).
  const exportTableData = useCallback(() => {
    const exportedData = metricsTableRef.current?.getExportedData() ?? [];
    // Over Time repeats the same metric column names under each segment super-header (which
    // getExportedData omits); prepend that super-header row when there are segments to label.
    const superHeaderRow =
      overTime && overTimePivot?.superHeaders.length
        ? [buildOverTimeSuperHeaderRow(overTimePivot.superHeaders, exportedData[0]?.length ?? 0)]
        : [];
    // Papa.unparse RFC-4180-quotes any comma-bearing value (e.g. a segment label), so cells survive
    // intact; empty cells render as '-' to match the on-screen table.
    const csvContent = Papa.unparse(
      [...superHeaderRow, ...exportedData].map(row => row.map(cell => cell ?? '-'))
    );
    // Collapse spaces and other filesystem-unsafe characters (e.g. in the preset name) to underscores.
    const fileName = `${company}_${tablePreset?.name ?? 'Table'}_${tableView}`.replace(
      /[^\w.-]+/g,
      '_'
    );
    download(csvContent, `${fileName}.csv`, 'text/csv');
    Mixpanel.track(MxE.ActionTriggered, { page: PAGE_NAMES.PACING, action: 'download table data' });
  }, [company, overTime, overTimePivot, tablePreset, tableView]);

  const editTablePresetModal = (showEditTablePresetModal || showNewTablePresetModal) &&
    tablePresetChanges && (
      <EditTablePresetModal
        columnMetaDataMap={pacingMetricColumnMetaDataMap}
        dimensionColumnMetaDataMap={dimensionColumnMetaDataMap}
        globalOptions={{ kpi, isDefault: false }}
        groupedColumnPicker={groupedColumnPicker}
        kpiMetaData={kpiMetaData}
        prefix={PACING_MEDIATYPE}
        onHide={() => {
          setShowEditTablePresetModal(false);
          setShowNewTablePresetModal(false);
          setTablePresetChanges(tablePreset);
        }}
        presetChanges={tablePresetChanges}
        onCustomMetricsChanged={() => {}}
        setPresetChanges={setTablePresetChanges as Dispatch<SetStateAction<MetricsTablePreset>>}
        saveAndApplyOnClick={handleSaveAndApply}
      />
    );

  const renameTablePresetModal = showRenameTablePresetModal && renameTarget && (
    <RenameModal
      applyOnClick={newName => {
        (async () => {
          const saved = await saveTablePreset({
            company,
            id: renameTarget.id,
            mediatype: PACING_MEDIATYPE,
            name: newName,
            temporary: renameTarget.temporary
          });
          setShowRenameTablePresetModal(false);
          applyTablePreset(saved.name, saved.id);
          Mixpanel.track(MxE.RecordSaved, { page: PAGE_NAMES.PACING, entity: 'table preset' });
        })();
      }}
      applyText='Save'
      cancelText='Discard'
      onCancel={() => setShowRenameTablePresetModal(false)}
      onHide={() => setShowRenameTablePresetModal(false)}
      placeholder='Enter new preset name'
      subTitle='New Table Preset Name'
      title={`Rename ${renameTarget.name}`}
    />
  );

  const tableFilterBar = (
    <div className='pacing-filter-bar-container'>
      <FilterBar
        hasAdvanced
        onFilter={f => setFilter({ filter: f })}
        options={
          tablePreset && tablePreset.dimensionColumns
            ? tablePreset.dimensionColumns.map(col => col.dimensionVarName)
            : []
        }
        lines={data.map(line => line.dimensions)}
      />
    </div>
  );

  const presetDropdown = (
    <PresetDropdown
      size={Size.MD}
      addPresetOnClick={handleAddPreset}
      addPresetText='Add New Table Preset'
      applyOnClick={preset => applyTablePreset(preset.name, preset.id)}
      cancelOnClick={() => {}}
      className={cn('pacing-table-preset-picker', {
        loading: fetchingTablePresets || !tablePresets
      })}
      deleteItemOnClick={presetID =>
        deleteTablePreset({ id: presetID, mediatype: PACING_MEDIATYPE })
      }
      editItemOnClick={handleEditPreset}
      label={
        tablePreset && tablePreset.id != null
          ? `Table Preset: ${tablePreset.temporary ? 'Temp' : tablePreset.name}`
          : 'Load Table Preset'
      }
      // Hide temporary presets (Temp Preview throwaways) from the list, except the one currently
      // applied.
      presets={(tablePresets ?? []).filter(
        preset => !preset.temporary || preset.id === tablePreset?.id
      )}
      renameItemOnClick={(preset: MetricsTablePreset) => {
        setRenameTarget(preset);
        setShowRenameTablePresetModal(true);
        Mixpanel.track(MxE.ModalOpened, { page: PAGE_NAMES.PACING, modal: 'rename table preset' });
      }}
      selectedPreset={tablePreset}
    />
  );

  // Summary | Over Time toggle, rendered to the left of the preset dropdown.
  const viewToggle = setTableView && (
    <SegmentedControlButton
      options={[TableView.SUMMARY, TableView.OVER_TIME]}
      selectedOption={tableView}
      onChange={setTableView}
    />
  );

  const downloadButton = (
    <IconButton
      design={Design.NEUTRAL}
      background={Background.LIGHT}
      size={Size.MD}
      disabled={!tableDataReady || !tablePreset}
      onClick={exportTableData}
      aria-label='Download table data'
      tooltip={{ text: 'Download table data' }}
    >
      <MdOutlineFileDownload />
    </IconButton>
  );

  // The controls row sits above the table, which fills the remaining height via `fillParentHeight`,
  // so it grows to the available space without spurious scrollbars.
  return (
    <div className='pacing-preset-table-dashboard' data-testid='pacing-table-dashboard'>
      {editTablePresetModal}
      {renameTablePresetModal}
      <div className='pacing-table-controls-container'>
        <div className='pacing-table-controls-row'>
          {tableFilterBar}
          <div className='pacing-table-controls'>
            {viewToggle}
            {presetDropdown}
            {downloadButton}
          </div>
        </div>
        <TimePeriodProgressIndicator progress={timePeriodProgress} />
      </div>
      <div className='pacing-metrics-table-and-footer'>
        <div className='pacing-metrics-table-container'>
          {!tableDataReady || !tablePreset ? (
            <Skeleton>
              <TableSkeleton />
            </Skeleton>
          ) : (
            <MetricsTableWithPresets
              ref={metricsTableRef}
              columnMetaDataMap={renderColumnMetaDataMap}
              data={overTime && overTimePivot ? overTimePivot.data : data}
              dataFetchKey={
                overTime && overTimePivot ? overTimePivot.dataFetchKey : summaryDataFetchKey
              }
              // Over-time filtering is applied to the raw rows before the pivot (above), so the
              // table itself doesn't re-filter the already-pivoted date rows.
              filter={overTime ? undefined : filter?.filter}
              hasAggregateData
              fillParentHeight
              preset={renderPreset ?? tablePreset}
            />
          )}
        </div>
      </div>
    </div>
  );
};
